import { useEffect, useState } from 'react';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Menu } from '../../componentes/Menu';
import { Paginacion } from '../../componentes/Paginacion';
import { Ventana } from '../../componentes/Ventana';
import { errorDeCampo, faltanDosPasos } from '../../logica/acceso';
import { estadoDeError } from '../../logica/estados';
import { usePaginacionCursor } from '../../logica/paginacion';
import { ERROR_MOTIVO, motivoValido, textoDeError } from '../negocios/logica';
import { SinPermiso } from '../SinPermiso';
import type { PropsSeccion } from '../tipos';
import { avisarColaCambiada } from './cifra';
import {
    avisoDeFacturacion,
    avisoDeMensajeria,
    avisoDeStripe,
    filtrosDe,
    juntar,
    leerCursores,
    lineaDeAviso,
    origenesDe,
    siguientesCursores,
    TEXTO_REINTENTAR_MENSAJERIA,
    textoResuelto,
    type AvisoDeLaCola,
    type Origen,
} from './logica';
import './errores.css';

/**
 * **Cola de errores** del backoffice (1.21, G187): una sola lista con los avisos de facturación de Reservaria, de Stripe
 * de los negocios y de los proveedores de mensajería que no se han podido aplicar, del más reciente al más antiguo, con
 * los filtros por origen (con su cifra), «Reintentar» y, en el «⋯», «Marcar como resuelto» con motivo. Solo el
 * Superadmin actúa; Soporte ve los avisos de Stripe de los negocios en lectura (G1138; roles-permisos, «Plataforma»). La
 * página junta las tres colas con su propio cursor cada una («Anterior / Siguiente», G121). El nombre del negocio llega en
 * cada aviso (`business_name`: facturación y Stripe, G1140; mensajería, G1174), sin pedirlo aparte. «Reintentar» en
 * la mensajería abre una ventana con motivo (`reprocessMessagingProviderEvent`, G1137).
 *
 * Cola, ventanas y avisos (excepto la de reintentar con motivo, que es provisional hasta C.64) siguen el prototipo.
 */
const POR_PAGINA = 25;
/** Ninguna de las colas del rol se puede leer. */
const SIN_PERMISO = Symbol('sin-permiso');

interface Pagina {
    avisos: AvisoDeLaCola[];
    siguiente: string | null;
}

export function Errores(p: PropsSeccion) {
    const { ops, superadmin, movil, onSesionCaducada, onFaltanDosPasos } = p;
    const avisar = useAvisoBreve();
    const pag = usePaginacionCursor();
    const [filtro, setFiltro] = useState(0);
    const [vuelta, setVuelta] = useState(0);
    const [estado, setEstado] = useState<{ clave: string; pagina: Pagina | null; error: unknown }>({ clave: '', pagina: null, error: null });
    const [reintentando, setReintentando] = useState<string | null>(null);
    const [resolver, setResolver] = useState<AvisoDeLaCola | null>(null);
    const [conMotivo, setConMotivo] = useState<AvisoDeLaCola | null>(null);
    const filtros = filtrosDe(superadmin);
    const clave = JSON.stringify([superadmin, pag.cursor, vuelta]);

    useEffect(() => {
        let vivo = true;
        const cursores = leerCursores(pag.cursor);
        const pedir = (o: Origen): Promise<{ avisos: AvisoDeLaCola[]; siguiente: string | null } | null> => {
            const c = cursores[o];
            if (c === false) {
                return Promise.resolve({ avisos: [], siguiente: null });
            }
            const pagina = { cursor: c, limit: POR_PAGINA };
            const sinPermiso = (e: unknown) => {
                // Una cola que el rol no puede leer no cuenta; si no puede leer ninguna, «sin permiso».
                if (e instanceof ErrorApi && e.estado === 403 && !faltanDosPasos(e)) {
                    return null;
                }
                throw e;
            };
            if (o === 'facturacion') {
                return ops.listBillingEvents(pagina).then((r) => ({ avisos: r.data.map(avisoDeFacturacion), siguiente: r.next_cursor }), sinPermiso);
            }
            if (o === 'stripe') {
                return ops.listStripeConnectEvents(pagina).then((r) => ({ avisos: r.data.map(avisoDeStripe), siguiente: r.next_cursor }), sinPermiso);
            }
            return ops.listMessagingProviderEvents(pagina).then((r) => ({ avisos: r.data.map(avisoDeMensajeria), siguiente: r.next_cursor }), sinPermiso);
        };
        const origenes = origenesDe(superadmin);
        Promise.all(origenes.map(pedir))
            .then((r) => {
                if (!vivo) {
                    return;
                }
                if (r.every((x) => x === null)) {
                    setEstado({ clave, pagina: null, error: SIN_PERMISO });
                    return;
                }
                const siguientes: Record<Origen, string | null> = { facturacion: null, stripe: null, mensajeria: null };
                origenes.forEach((o, i) => {
                    siguientes[o] = r[i]?.siguiente ?? null;
                });
                setEstado({ clave, pagina: { avisos: juntar(...r.map((x) => x?.avisos ?? [])), siguiente: siguientesCursores(siguientes) }, error: null });
            })
            .catch((error: unknown) => {
                if (!vivo) {
                    return;
                }
                if (error instanceof ErrorApi && error.estado === 401) {
                    onSesionCaducada();
                    return;
                }
                if (faltanDosPasos(error)) {
                    onFaltanDosPasos();
                    return;
                }
                setEstado({ clave, pagina: null, error });
            });
        return () => {
            vivo = false;
        };
    }, [ops, superadmin, pag.cursor, vuelta, clave, onSesionCaducada, onFaltanDosPasos]);

    const lista = estado.clave === clave ? estado.pagina : null;
    const error = estado.clave === clave ? estado.error : null;
    const elegido = filtros[filtro]?.clave ?? null;
    const avisos = (lista?.avisos ?? []).filter((a) => elegido === null || a.origen === elegido);
    const ahora = new Date();

    const recargar = () => {
        setVuelta((v) => v + 1);
        avisarColaCambiada();
    };

    const reintentar = (a: AvisoDeLaCola) => {
        if (reintentando !== null) {
            return;
        }
        if (a.origen === 'mensajeria') {
            setConMotivo(a);
            return;
        }
        setReintentando(a.clave);
        const peticion = a.origen === 'facturacion' ? ops.reprocessBillingEvent(a.id) : ops.reprocessStripeConnectEvent(a.id);
        void peticion
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e) });
            })
            .finally(() => {
                setReintentando(null);
                recargar();
            });
    };

    if (error === SIN_PERMISO || (error !== null && estadoDeError(error).tipo === 'sin-permiso')) {
        return <SinPermiso seccion="Cola de errores" superadmin={superadmin} movil={movil} onIr={null} />;
    }
    return (
        <>
            <Filtros
                items={filtros.map((f) => ({ etiqueta: f.etiqueta, cifra: lista === null ? null : lista.avisos.filter((a) => f.clave === null || a.origen === f.clave).length }))}
                valor={filtro}
                etiqueta="Origen de los errores"
                onCambio={setFiltro}
            />
            {error !== null ? (
                <EstadoError seccion="Cola de errores" codigoSoporte={estadoDeError(error).codigoSoporte} movil={movil} onReintentar={() => {
                    setVuelta((v) => v + 1);
                }} />
            ) : lista === null ? (
                <EstadoCargando seccion="Cola de errores" />
            ) : (
                <div className="rv-bo-errores">
                    {avisos.length > 0 ? (
                        <div className="rv-bo-errores__lista" role="list" aria-label="Avisos sin aplicar">
                            {avisos.map((a, i) => (
                                <div key={a.clave} role="listitem" className="rv-bo-aviso" style={{ animationDelay: `${String(i * 60)}ms` }}>
                                    <span className={`rv-bo-aviso__baldosa${a.urgente ? ' rv-bo-aviso__baldosa--urgente' : ''}`}>
                                        <Icono nombre={a.icono} />
                                    </span>
                                    <span className="rv-bo-aviso__textos">
                                        <span className="rv-bo-aviso__titulo">
                                            <strong>{a.titulo}</strong>
                                            {a.urgente && <Chip estado="problem">Urgente</Chip>}
                                        </span>
                                        <span className="rv-bo-aviso__detalle">{a.detalle}</span>
                                        <span className="rv-bo-aviso__linea">{lineaDeAviso(a, a.negocioNombre ?? undefined, ahora)}</span>
                                    </span>
                                    {superadmin && (
                                        <span className={`rv-bo-aviso__acciones${movil ? ' rv-bo-aviso__acciones--movil' : ''}`}>
                                            {/* La de mensajería pide motivo (G1137): abre su ventana. */}
                                            <button type="button" className="rv-bo-aviso__reintentar" disabled={reintentando !== null} onClick={() => {
                                                reintentar(a);
                                            }}>
                                                <Icono nombre="refresh-cw" className={reintentando === a.clave ? 'rv-bo-aviso__gira' : undefined} />
                                                <span>{reintentando === a.clave ? 'Reintentando…' : 'Reintentar'}</span>
                                            </button>
                                            <Menu
                                                etiqueta={`Más opciones de «${a.titulo}»`}
                                                movil={movil}
                                                opciones={[
                                                    {
                                                        clave: 'resuelto',
                                                        texto: 'Marcar como resuelto',
                                                        icono: 'check',
                                                        detalle: 'Sale de la cola sin reprocesarlo. Pide motivo.',
                                                        onClick: () => {
 setResolver(a);
},
                                                    },
                                                ]}
                                            />
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rv-bo-errores__vacia">
                            <span>
                                <Icono nombre="inbox" />
                            </span>
                            <strong>No hay errores pendientes</strong>
                            <em>Cuando un aviso no se pueda aplicar, aparecerá aquí.</em>
                        </div>
                    )}
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={lista.siguiente !== null} movil={movil} onAnterior={pag.anterior} onSiguiente={() => {
                        pag.siguiente(lista.siguiente);
                    }} />
                </div>
            )}
            {conMotivo !== null && (
                <VentanaReintentar
                    {...p}
                    aviso={conMotivo}
                    onCerrar={() => {
                        setConMotivo(null);
                    }}
                    onHecho={() => {
                        setConMotivo(null);
                        recargar();
                    }}
                />
            )}
            {resolver !== null && (
                <VentanaResuelto
                    {...p}
                    aviso={resolver}
                    onCerrar={() => {
                        setResolver(null);
                    }}
                    onHecho={() => {
                        setResolver(null);
                        recargar();
                    }}
                />
            )}
        </>
    );
}

/** «Marcar como resuelto» (prototipo `v.close`): con motivo de 10 a 500 letras, que queda en Auditoría (G187). */
function VentanaResuelto({ ops, aviso, movil, onCerrar, onHecho }: PropsSeccion & { aviso: AvisoDeLaCola; onCerrar: () => void; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errorApi, setErrorApi] = useState<string | null>(null);
    const error = errorApi ?? (intentado && !motivoValido(motivo) ? ERROR_MOTIVO : null);

    const guardar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (!motivoValido(motivo)) {
            return;
        }
        setOcupado(true);
        const datos = { reason: motivo.trim() };
        const peticion =
            aviso.origen === 'facturacion' ? ops.closeBillingEvent(aviso.id, datos) : aviso.origen === 'stripe' ? ops.closeStripeConnectEvent(aviso.id, datos) : ops.closeMessagingProviderEvent(aviso.id, datos);
        peticion.then(onHecho).catch((e: unknown) => {
            setOcupado(false);
            const delMotivo = errorDeCampo(e, 'reason');
            if (delMotivo !== null) {
                setErrorApi(delMotivo);
                return;
            }
            avisar({ tipo: 'error', texto: textoDeError(e) });
        });
    };

    return (
        <Ventana abierta titulo="Marcar como resuelto" texto={textoResuelto(aviso.origen)} accion="Resuelto" accionIcono="check" ocupado={ocupado} movil={movil} onAccion={guardar} onCerrar={onCerrar}>
            <Campo
                etiqueta="Motivo"
                valor={motivo}
                onChange={(v) => {
                    setMotivo(v);
                    setErrorApi(null);
                }}
                error={error}
                ayuda="Por ejemplo: «Corregido a mano en Stripe»."
                max={500}
                onEnter={guardar}
            />
        </Ventana>
    );
}

/** «Reintentar» un aviso de mensajería (`reprocessMessagingProviderEvent`, G1137): pide el motivo, que queda en Auditoría. */
// C.64: la ventana del dibujo 1.21.
function VentanaReintentar({ ops, aviso, movil, onCerrar, onHecho }: PropsSeccion & { aviso: AvisoDeLaCola; onCerrar: () => void; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errorApi, setErrorApi] = useState<string | null>(null);
    const error = errorApi ?? (intentado && !motivoValido(motivo) ? ERROR_MOTIVO : null);

    const guardar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (!motivoValido(motivo)) {
            return;
        }
        setOcupado(true);
        ops.reprocessMessagingProviderEvent(aviso.id, { reason: motivo.trim() })
            .then(onHecho)
            .catch((e: unknown) => {
                setOcupado(false);
                const delMotivo = errorDeCampo(e, 'reason');
                if (delMotivo !== null) {
                    setErrorApi(delMotivo);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };

    return (
        <Ventana abierta titulo="Reintentar el aviso" texto={TEXTO_REINTENTAR_MENSAJERIA} accion="Reintentar" accionIcono="refresh-cw" ocupado={ocupado} ocupadoTexto="Reintentando…" movil={movil} onAccion={guardar} onCerrar={onCerrar}>
            <Campo
                etiqueta="Motivo"
                valor={motivo}
                onChange={(v) => {
                    setMotivo(v);
                    setErrorApi(null);
                }}
                error={error}
                ayuda="Obligatorio. Queda en Auditoría. Por ejemplo: «YCloud ya ha dado de alta el mensaje»."
                max={500}
                onEnter={guardar}
            />
        </Ventana>
    );
}
